import { useRect } from '@reach/rect';
import { useCombobox, UseComboboxStateChange } from 'downshift';
import { AnimatePresence } from 'framer-motion';
import debounce from 'lodash/debounce';
import { FC, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { globalTheme } from 'styles/global-theme';
import { IconButton } from '../icon-button';
import {
  getStateValues,
  InputProps,
  InputStyled,
  InputWrapper,
  Label,
  StateIcon,
  StyledIcon,
  variants,
  Wrapper,
} from '../input';
import { InputMessage } from '../input-message';

export type ComboboxItem = { id: string; value: string };

export interface ComboboxProps extends Omit<InputProps, 'as' | 'id' | 'value'> {
  label: string;
  items: ComboboxItem[];
  onInputValueChange?: (changes: UseComboboxStateChange<ComboboxItem>) => void;
  onSelectedItemChange?: (
    changes: UseComboboxStateChange<ComboboxItem>
  ) => void;
  onReset?: () => void;
  isLoading?: boolean;
  initialValue?: ComboboxItem;
  value?: ComboboxItem;
  textAlign?: string;
  bookingCell?: boolean;
  layout?: 'default' | 'spaced';
  showClearIcon?: boolean;
}

export const ComboboxList = styled.ul<{ textAlign: string }>`
  position: fixed;
  z-index: ${globalTheme.zIndices.listBox};
  max-height: 200px;
  overflow: auto;
  font-size: ${globalTheme.fontSizes.default};
  background-color: ${globalTheme.elements.combobox.backgroundColor};
  border: 0;
  border-radius: 8px;
  box-shadow: 0 8px 16px rgba(17, 17, 17, 0.2);
  transform: translateY(calc(${globalTheme.space[0]} / 2));
  ${({ textAlign }) => {
    if (textAlign) {
      return css`
        text-align: ${textAlign};
      `;
    }
  }}
`;

export const ComboboxListItem = styled.li<{
  $bookingCell?: boolean;
  $isSelected?: boolean;
}>`
  padding: ${globalTheme.space[1]};
  line-height: ${globalTheme.lineHeights.heading};
  font-weight: ${({ $isSelected }) =>
    $isSelected ? globalTheme.fontWeights.bold : globalTheme.fontWeights.light};
  background-color: ${({ $isSelected }) =>
    $isSelected
      ? globalTheme.elements.combobox.selectedItemBackgroundColor
      : globalTheme.elements.combobox.backgroundColor};
  cursor: default;
  transition: ${globalTheme.transitions.fast};
  font-size: ${({ $bookingCell }) =>
    $bookingCell
      ? globalTheme.fontSizes.bookingMobile
      : globalTheme.fontSizes.default};

  &[aria-selected='true'] {
    color: ${globalTheme.elements.combobox.resultHoverColor};
    background-color: ${globalTheme.elements.combobox
      .resultHoverBackgroundColor};
  }
`;

const ComboboxWrapper = styled(InputWrapper)<{ $bookingCell?: boolean }>`
  position: relative;
  min-height: ${(props) => (!props.$bookingCell ? '32px' : 'auto')};

  /* TODO: Fix this, z-index with input wrapper */
  &::before {
    position: absolute;
    top: ${globalTheme.space[0]};
    left: -2px;
    z-index: -1;
    width: calc(100% + 4px);
    height: 100%;
    background-color: ${globalTheme.elements.combobox.inputBackgroundColor};
    opacity: 0;
    content: '';
    pointer-events: none;
  }

  &[aria-expanded='true']::before {
    opacity: 1;
  }
`;

const Selection = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  gap: ${globalTheme.space[0]};
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 ${globalTheme.space[0]};
  background-color: ${globalTheme.elements.combobox
    .selectedInputBackgroundColor};
  border-radius: inherit;
`;

const ComboboxTextInput = styled(InputStyled)<{
  $bookingCell?: boolean;
  updateError?: boolean;
  textAlign?: string;
  $layout?: 'default' | 'spaced';
}>`
  ${({ $bookingCell, $layout }) => {
    if ($layout === 'spaced') {
      return css`
        height: 44px;
      `;
    }
    if (!$bookingCell) {
      return css`
        padding: 4px 8px;
        font-weight: ${globalTheme.fontWeights.light};
        border-radius: 6px;
        && {
          height: 100%;
        }

        textarea& {
          resize: none;
        }
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      `;
    }

    if ($bookingCell) {
      return css`
        font-size: ${globalTheme.fontSizes.bookingMobile};
      `;
    }
  }}
  ${({ textAlign }) => {
    if (textAlign) {
      return css`
        text-align: ${textAlign};
      `;
    }
  }}
`;

export const Combobox: FC<ComboboxProps> = ({
  label,
  message,
  state,
  icon,
  items: listItems = [],
  onSelectedItemChange,
  onReset,
  isLoading,
  initialValue: passedInitialValue,
  value,
  placeholder,
  textAlign = 'left',
  bookingCell = false,
  disabled,
  layout = 'default',
  showClearIcon = true,
}) => {
  const initialValue = useRef(passedInitialValue);

  const [items, setItems] = useState(listItems);
  const {
    isOpen,
    selectedItem,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    inputValue,
    reset,
    selectItem,
    setInputValue,
  } = useCombobox({
    items,
    onInputValueChange({ inputValue }) {
      const filteredItems = filterList(inputValue);
      setItems(filteredItems);
    },
    onSelectedItemChange,
    itemToString: (item) => item?.value || '',
    initialSelectedItem: initialValue.current,
  });

  const { stateColor, stateIcon } = getStateValues(state);

  // Popper reference element
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);

  // Observe the bounding rect of the input wrapper so that the list can be positioned
  const wrapperRef = useRef<HTMLDivElement>(null);
  const rect = useRect(wrapperRef, { observe: isOpen });

  // Maintain a reference to the selected item for use in onBlur since the call is debounced
  const selectedItemRef = useRef<ComboboxItem | null>(selectedItem);
  useEffect(() => {
    if (selectedItem?.id !== selectedItemRef.current?.id) {
      selectedItemRef.current = selectedItem;
    }
  }, [selectedItem]);

  // Reset the input
  const handleReset = () => {
    reset();
    setInputValue('');
    onReset && onReset();
  };

  // Reset on blur if the user has not made a selection
  const handleBlur = () => {
    if (!selectedItemRef.current) {
      reset();
    } else {
      setInputValue(selectedItemRef.current.value);
    }
  };

  // Handle selected item based on external changes to the value prop
  useEffect(() => {
    if (value !== undefined && selectedItem?.id !== value?.id) {
      if (value?.id) {
        // Set value
        selectItem(value);
      } else {
        // Reset if value is empty
        reset();
      }
    }
  }, [reset, selectItem, selectedItem?.id, value]);

  /** Filter the list with the input value */
  const filterList = (inputValue: string | undefined) => {
    if (!inputValue || inputValue.trim() === '') {
      return listItems;
    }

    return listItems.filter((item) =>
      item.value.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  return (
    <Wrapper ref={setReferenceElement}>
      {label && <Label {...getLabelProps()}>{label}</Label>}
      <div
        ref={wrapperRef}
        data-cy={`${label.replace(/\s+|[,\/]/g, '')}InputBox`}
      >
        <ComboboxWrapper $bookingCell={bookingCell} color={stateColor}>
          {icon && <StyledIcon component={icon} size={2} />}
          <ComboboxTextInput
            {...getInputProps({
              ...{
                bookingCell,
                textAlign,
                value: inputValue,
              },
            })}
            $bookingCell={bookingCell}
            $layout={layout}
            data-state={state}
            onBlur={debounce(handleBlur, 250)}
            disabled={disabled}
            placeholder={selectedItem?.value || placeholder}
          />
          {selectedItem && showClearIcon && (
            <Selection>
              <IconButton
                icon='CancelIcon'
                size={bookingCell ? 1 : 0}
                onClick={handleReset}
                type='button'
                buttonColor={false}
                aria-label='Clear selection'
              />
            </Selection>
          )}
          <AnimatePresence>
            {state && stateIcon && !selectedItem && (
              <StateIcon
                component={stateIcon}
                color={stateColor}
                size={1}
                variants={variants}
                initial='hidden'
                exit='hidden'
                animate='visible'
              />
            )}
          </AnimatePresence>
        </ComboboxWrapper>
      </div>

      <ComboboxList
        {...getMenuProps()}
        textAlign={textAlign}
        style={{
          width: rect?.width,
          top: (rect?.top || 0) + (rect?.height || 0),
          left: rect?.left,
        }}
      >
        {isLoading && !selectedItem && (
          <ComboboxListItem $bookingCell={bookingCell}>
            Loading...
          </ComboboxListItem>
        )}
        {inputValue &&
          items.length === 0 &&
          !isLoading &&
          (!selectedItem || filterList(inputValue).length === 0) && (
            <ComboboxListItem $bookingCell={bookingCell}>
              No results
            </ComboboxListItem>
          )}
        {isOpen &&
          !isLoading &&
          items.map((item, index) => (
            <ComboboxListItem
              key={`${item.id}`}
              {...getItemProps({ item, index })}
              $bookingCell={bookingCell}
              $isSelected={selectedItem?.value === item.value}
            >
              {item.value}
            </ComboboxListItem>
          ))}
      </ComboboxList>

      <AnimatePresence>
        {message && (
          <InputMessage
            backgroundColor={stateColor}
            referenceElement={referenceElement}
          >
            {message}
          </InputMessage>
        )}
      </AnimatePresence>
    </Wrapper>
  );
};
